// @ts-check

class FinstralGlobalMenu extends HTMLElement {
	/** @type {string | null} */
	#activeSubmenuId = null;
	#isMenuVisible = false;
	/** @type {ResizeObserver | null} */
	#resizeObserver = null;
	/** @type {AbortController | null} */
	#abortController = null;
	/** @type {HTMLElement | null} */
	#desktopTrigger = null;

	static #OVERVIEW_PANEL_ID = "menu-mobile-overview-panel";
	static #FOCUSABLE_SELECTOR = "a[href], button:not([disabled])";
	static #TABBABLE_SELECTOR =
		'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

	constructor() {
		super();
	}

	get isMenuVisible() {
		return this.#isMenuVisible;
	}

	#setupResizeObserver() {
		this.#resizeObserver = new ResizeObserver(() => {
			requestAnimationFrame(() => {
				this.#updateSubmenusInset();
			});
		});

		const menuDialog = this.querySelector(".Menu-dialog");
		if (menuDialog) {
			this.#resizeObserver.observe(menuDialog);
		}
	}

	#updateSubmenusInset() {
		/** @type {HTMLElement[]} */
		const submenus = Array.from(this.querySelectorAll(".js-MenuSubmenu"));
		submenus.forEach((submenu) => {
			submenu.dataset.inset = String(-submenu.offsetLeft);
		});

		if (this.#activeSubmenuId) {
			this.#updateTrackPosition(this.#activeSubmenuId);
		}
	}

	/**
	 * Updates the track position for the active submenu.
	 * @param {string} submenuId - The ID of the submenu to position
	 * @returns {void}
	 */
	#updateTrackPosition(submenuId) {
		const menuSectionsTrack = /** @type {HTMLElement | null} */ (
			this.querySelector(".Menu-sectionsTrack")
		);
		const targetSubmenu = /** @type {HTMLElement | null} */ (
			this.querySelector(`#${submenuId}`)
		);

		if (targetSubmenu && menuSectionsTrack) {
			menuSectionsTrack.style.setProperty(
				"--Menu-track-inset",
				`${targetSubmenu.dataset.inset}px`,
			);
		}
	}

	connectedCallback() {
		this.#abortController?.abort();
		this.#abortController = new AbortController();
		this.#setupResizeObserver();
		this.#syncInactivePanels();
		this.#addEventListeners();
	}

	disconnectedCallback() {
		this.#resizeObserver?.disconnect();
		this.#abortController?.abort();
		this.#abortController = null;
	}

	/**
	 * Sets the visibility of the menu.
	 * @param {boolean} isVisible - Whether the menu should be visible
	 * @param {HTMLElement} [menuOpenTrigger] - The element that triggered the menu open
	 * @returns {void}
	 */
	setMenuVisibility(isVisible, menuOpenTrigger) {
		this.#isMenuVisible = isVisible;

		const menuDialog = /** @type {HTMLElement | null} */ (
			this.querySelector(".js-Menu-dialog")
		);
		const menuDialogBackdrop = /** @type {HTMLElement | null} */ (
			document.querySelector(".js-HeaderGlobal-menuBackdrop")
		);
		const headerGlobal = /** @type {HTMLElement | null} */ (
			document.querySelector(".js-HeaderGlobal")
		);

		if (menuOpenTrigger) {
			menuOpenTrigger.setAttribute("aria-expanded", String(isVisible));
		}

		if (!isVisible) {
			/** @type {HTMLElement[]} */
			const expandedMenuButtons = Array.from(
				this.querySelectorAll('.js-MenuButton[aria-expanded="true"]'),
			);
			expandedMenuButtons.forEach((button) => {
				button.setAttribute("aria-expanded", "false");
			});

			this.#resetToOverviewPanel(menuDialog);
		}

		if (isVisible) {
			this.#updateViewportOffset(headerGlobal);
		}

		if (menuDialog) {
			menuDialog.hidden = !isVisible;
			menuDialog.classList.toggle("u-modal-open", isVisible);
		}

		if (menuDialogBackdrop) {
			menuDialogBackdrop.hidden = !isVisible;
		}

		if (headerGlobal) {
			headerGlobal.classList.toggle("menu-visible", isVisible);
		}

		this.dispatchEvent(
			new CustomEvent("finstral-menu-visibility-change", {
				detail: { open: isVisible },
			}),
		);
	}

	/**
	 * The dialog fills the viewport below the header, but the header is not
	 * always at the top of the viewport: logged-in users have the Drupal
	 * toolbar (and edit bar) above it. Without this offset the bottom of the
	 * dialog sits below the fold, and as the page is locked while the menu is
	 * open, its last links cannot be reached (FINSTRAL-2372).
	 * @param {HTMLElement | null} headerGlobal - The global header
	 * @returns {void}
	 */
	#updateViewportOffset(headerGlobal) {
		const offset = Math.max(0, headerGlobal?.getBoundingClientRect().top ?? 0);
		this.style.setProperty("--js-Menu-viewport-offset", `${offset}px`);
	}

	/**
	 * Puts the menu back on its first level, so it reopens there. Otherwise it
	 * reopens on the last submenu, measured while the dialog was hidden: focus
	 * then scrolls the dialog sideways onto an empty panel (FINSTRAL-2339).
	 * @param {HTMLElement | null} menuDialog - The menu dialog
	 * @returns {void}
	 */
	#resetToOverviewPanel(menuDialog) {
		this.#activeSubmenuId = null;
		this.#syncInactivePanels();

		this.querySelectorAll(".js-MenuSubmenu.is-active").forEach((submenu) => {
			submenu.classList.remove("is-active");
		});

		const track = /** @type {HTMLElement | null} */ (
			this.querySelector(".Menu-sectionsTrack")
		);
		track?.style.removeProperty("--Menu-track-inset");
		track?.style.removeProperty("--js-Menu-track-block-size");

		if (menuDialog) {
			menuDialog.scrollLeft = 0;
		}
	}

	/**
	 * Toggles the visibility of the menu.
	 * @param {HTMLElement} [menuOpenTrigger] - The element that triggered the menu open
	 * @returns {void}
	 */
	#toggleMenuVisibility(menuOpenTrigger) {
		this.setMenuVisibility(!this.#isMenuVisible, menuOpenTrigger);
	}

	/**
	 * Handles close button click events.
	 * @param {Event} event - The click event
	 * @returns {void}
	 */
	#handleCloseEvent = (event) => {
		const target = /** @type {HTMLElement | null} */ (event.target);

		if (!target) {
			return;
		}

		const closeButton = target.classList.contains("js-Menu-dialogToggle")
			? target
			: target.closest(".js-Menu-dialogToggle");

		if (closeButton) {
			this.#closeAndRestoreFocus();
		}
	};

	/**
	 * Handles clicks outside the menu.
	 * @param {Event} event - The click event
	 * @returns {void}
	 */
	#handleClickOutside = (event) => {
		const target = /** @type {Node | null} */ (event.target);

		if (!target || !this.#isMenuVisible) {
			return;
		}

		const mql = window.matchMedia("(width >= 64rem)");
		if (target !== this && !this.contains(target) && mql.matches) {
			this.#toggleMenuVisibility();
			this.#resetTopLevelMenuItems();
		}
	};

	/**
	 * Handles keyboard events (Escape key).
	 * @param {KeyboardEvent} event - The keyboard event
	 * @returns {void}
	 */
	#handleKeyboardEvents = (event) => {
		const mql = window.matchMedia("(width >= 64rem)");

		if (!this.#isMenuVisible) {
			return;
		}

		if (mql.matches) {
			if (event.key === "Escape") {
				this.#closeAndRestoreFocus();
			} else if (event.key === "Tab") {
				this.#handleDesktopTab(event);
			}
			return;
		}

		if (event.key === "Escape") {
			// An open language dropdown closes itself on Escape (on keyup), so let
			// it do that before a second Escape closes the whole menu.
			if (
				this.querySelector(
					'.js-FinstralCustomDisclosure-toggle[aria-expanded="true"]',
				)
			) {
				return;
			}
			this.setMenuVisibility(false, this.#mobileToggle ?? undefined);
			this.#mobileToggle?.focus();
			return;
		}

		if (event.key !== "Tab") {
			return;
		}

		// The mobile dialog is modal but sits before its toggle in the DOM, so
		// Tab would otherwise leave for the page behind it. Loop between the
		// active panel and the toggle instead.
		const focusables = this.#getActivePanelFocusables();
		const edge = event.shiftKey ? focusables[0] : focusables.at(-1);

		if (edge && document.activeElement === edge) {
			event.preventDefault();
			this.#mobileToggle?.focus();
		}
	};

	/**
	 * The open panel is rendered after all top-level buttons in the DOM, so
	 * native Tab order would run through every top-level button before the
	 * panel and then leave the menu. Follow the visual order instead: each
	 * top-level button, with the open panel's items right after the one that
	 * opened it, then the close button, then on out of the menu.
	 * @param {KeyboardEvent} event - The Tab keydown event
	 * @returns {void}
	 */
	#handleDesktopTab(event) {
		const order = this.#getDesktopTabOrder();
		const index = order.indexOf(
			/** @type {HTMLElement} */ (document.activeElement),
		);

		if (index === -1) {
			return;
		}

		const next = order[event.shiftKey ? index - 1 : index + 1];

		if (next) {
			event.preventDefault();
			next.focus();
			return;
		}

		// Past the close button: skip the rest of the menu's DOM (the panel
		// already visited) and continue with whatever follows the menu.
		if (!event.shiftKey) {
			const after = this.#getFirstTabbableAfterMenu();
			if (after) {
				event.preventDefault();
				after.focus();
			}
		}
	}

	/**
	 * @returns {HTMLElement[]} Desktop menu elements in visual Tab order
	 */
	#getDesktopTabOrder() {
		/** @type {HTMLElement[]} */
		const topLevelButtons = Array.from(
			this.querySelectorAll(".Menu-primary--desktop .js-MenuButton"),
		);
		const closeButton = /** @type {HTMLElement | null} */ (
			this.querySelector(".js-Menu-dialogToggle")
		);

		const order = topLevelButtons.flatMap((button) =>
			button === this.#desktopTrigger
				? [button, ...this.#getActivePanelFocusables()]
				: [button],
		);

		if (closeButton && closeButton.getClientRects().length > 0) {
			order.push(closeButton);
		}

		return order;
	}

	/**
	 * @returns {HTMLElement | null} The first tabbable element after the menu
	 */
	#getFirstTabbableAfterMenu() {
		/** @type {HTMLElement[]} */
		const tabbables = Array.from(
			document.querySelectorAll(FinstralGlobalMenu.#TABBABLE_SELECTOR),
		);

		return (
			tabbables.find(
				(element) =>
					!this.contains(element) &&
					this.compareDocumentPosition(element) &
						Node.DOCUMENT_POSITION_FOLLOWING &&
					element.tabIndex >= 0 &&
					!element.closest("[inert]") &&
					element.getClientRects().length > 0,
			) ?? null
		);
	}

	/**
	 * The header toggle that opens the menu in the mobile layout.
	 * @returns {HTMLElement | null}
	 */
	get #mobileToggle() {
		return document.querySelector(`[aria-controls="${this.id}"]`);
	}

	/**
	 * Visible, focusable elements of the panel currently shown.
	 * @returns {HTMLElement[]}
	 */
	#getActivePanelFocusables() {
		const activeId =
			this.#activeSubmenuId ?? FinstralGlobalMenu.#OVERVIEW_PANEL_ID;
		const panel = this.querySelector(`#${activeId}`);

		if (!panel) {
			return [];
		}

		/** @type {HTMLElement[]} */
		const focusables = Array.from(
			panel.querySelectorAll(FinstralGlobalMenu.#FOCUSABLE_SELECTOR),
		);
		return focusables.filter((element) => element.getClientRects().length > 0);
	}

	/**
	 * Moves focus to the first or last item of the panel currently shown. Used
	 * by the header toggle to hand focus into the mobile dialog.
	 * @param {"first" | "last"} position - Which end of the panel to focus
	 * @returns {void}
	 */
	focusActivePanel(position) {
		const focusables = this.#getActivePanelFocusables();
		const target = position === "first" ? focusables[0] : focusables.at(-1);

		target?.focus();
	}

	/**
	 * Closes the desktop menu once keyboard focus moves outside of it, so the
	 * open panel does not linger over the page while the user tabs on.
	 * @param {FocusEvent} event - The focusout event
	 * @returns {void}
	 */
	#handleFocusOut = (event) => {
		const nextFocused = /** @type {Node | null} */ (event.relatedTarget);
		const mql = window.matchMedia("(width >= 64rem)");

		if (
			!nextFocused ||
			this.contains(nextFocused) ||
			!this.#isMenuVisible ||
			!mql.matches
		) {
			return;
		}

		this.#toggleMenuVisibility();
		this.#resetTopLevelMenuItems();
	};

	/**
	 * Closes the menu and hands focus back to the top-level item that opened
	 * it, because the element that had focus is about to be hidden.
	 * @returns {void}
	 */
	#closeAndRestoreFocus() {
		const hadFocus = this.contains(document.activeElement);

		this.#toggleMenuVisibility();
		this.#resetTopLevelMenuItems();

		if (hadFocus) {
			this.#desktopTrigger?.focus();
		}
	}

	#handleClick = (/** @type {Event} */ event) => {
		const eventTarget = /** @type {HTMLElement | null} */ (event.target);

		if (!eventTarget) {
			return;
		}

		const target = eventTarget.classList.contains("js-MenuButton")
			? eventTarget
			: eventTarget.closest(".js-MenuButton");

		this.#handleCloseEvent(event);

		const mql = window.matchMedia("(width >= 64rem)");
		if (target && !this.#isMenuVisible && mql.matches) {
			this.#toggleMenuVisibility(/** @type {HTMLElement} */ (target));
		}

		if (target?.closest(".Menu-primary--desktop")) {
			this.#desktopTrigger = /** @type {HTMLElement} */ (target);
		}

		this.#updateSubmenusInset();

		/** @type {HTMLElement[]} */
		const submenus = Array.from(this.querySelectorAll(".js-MenuSubmenu"));
		const targetSubmenu = submenus.find(
			(submenu) => submenu.id === target?.getAttribute("aria-controls"),
		);

		if (targetSubmenu) {
			requestAnimationFrame(() => {
				this.#setActiveSubmenu(targetSubmenu);
			});
		}
	};

	#resetTopLevelMenuItems() {
		/** @type {HTMLElement[]} */
		const menuTopLevelPrimaryDesktop = Array.from(
			this.querySelectorAll(".Menu-primary--desktop .js-MenuButton"),
		);

		if (!menuTopLevelPrimaryDesktop) {
			return;
		}

		menuTopLevelPrimaryDesktop.forEach((item) => {
			item.classList.remove("RichMenuItem--active");
			item.setAttribute("aria-expanded", "false");
		});
	}

	/**
	 * @param {string} itemId
	 */
	#setActiveTopLevelMenuItem(itemId) {
		const item = this.querySelector(`[aria-controls="${itemId}"]`);

		this.#resetTopLevelMenuItems();

		if (item) {
			item.classList.add("RichMenuItem--active");

			if (this.#isMenuVisible && item.closest(".Menu-primary--desktop")) {
				item.setAttribute("aria-expanded", "true");
			}
		}
	}

	/**
	 * @param {HTMLElement} submenu
	 */
	#setActiveSubmenu(submenu) {
		const previousSubmenuId = this.#activeSubmenuId;

		this.#activeSubmenuId = submenu.id;
		this.#syncInactivePanels();

		/** @type {HTMLElement[]} */
		const submenus = Array.from(this.querySelectorAll(".js-MenuSubmenu"));
		submenus.forEach((_submenu) => {
			_submenu.classList.remove("is-active");
		});

		submenu.classList.add("is-active");
		this.#setActiveTopLevelMenuItem(submenu.id);

		const submenuHeight = submenu.getBoundingClientRect().height;
		const track = /** @type {HTMLElement} */ (
			this.querySelector(".Menu-sectionsTrack")
		);
		track.style.setProperty("--js-Menu-track-block-size", `${submenuHeight}px`);

		// The new height can show or hide the dialog's scrollbar (on platforms
		// where it takes up space), which shifts every panel sideways. Measure
		// the offsets only now, so the track is not painted with stale ones.
		this.#updateSubmenusInset();

		this.#focusPanel(submenu, previousSubmenuId);
	}

	/**
	 * All panels sit side by side in one track and only the active one is
	 * visible. Mark the others inert so Tab cannot reach links the user cannot
	 * see: focusing one would also scroll the dialog sideways onto it.
	 * @returns {void}
	 */
	#syncInactivePanels() {
		const activeId =
			this.#activeSubmenuId ?? FinstralGlobalMenu.#OVERVIEW_PANEL_ID;

		/** @type {HTMLElement[]} */
		const submenus = Array.from(this.querySelectorAll(".js-MenuSubmenu"));
		submenus.forEach((submenu) => {
			submenu.inert = submenu.id !== activeId;
		});
	}

	/**
	 * Moves focus into a newly shown panel. Going back lands on the item that
	 * leads to the panel just left, otherwise on the panel's first item.
	 * @param {HTMLElement} submenu - The panel that was just shown
	 * @param {string | null} previousSubmenuId - The panel shown before it
	 * @returns {void}
	 */
	#focusPanel(submenu, previousSubmenuId) {
		const returnTarget = previousSubmenuId
			? /** @type {HTMLElement | null} */ (
					submenu.querySelector(`[aria-controls="${previousSubmenuId}"]`)
				)
			: null;
		if (returnTarget) {
			returnTarget.focus();
		} else {
			this.focusActivePanel("first");
		}
	}

	#addEventListeners() {
		const signal = this.#abortController?.signal;
		if (!signal) {
			return;
		}

		this.addEventListener("click", this.#handleClick, { signal });
		this.addEventListener("keydown", this.#handleKeyboardEvents, { signal });
		this.addEventListener("focusout", this.#handleFocusOut, { signal });
		document.addEventListener("click", this.#handleClickOutside, { signal });
	}
}

customElements.define("finstral-global-menu", FinstralGlobalMenu);
